import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import settingService from "api/settingsService";
import Spinner from "components/Spinner";
import { BaeOperationStatusEnum } from "dto/Settings/BaeOperationStatusEnum";
import { EDeliveryAddressStatus } from "dto/Settings/EDeliveryAddressStatus";
import {
  EEDeliveryAddressPrivilege,
  IEDeliveryAddress,
  IEDeliveryAddressUserPrivileges,
} from "dto/Settings/IEDeliveryAddress";
import { IEDelvieryRequest } from "dto/Settings/IEDelvieryRequest";
import moment from "moment";
import CreateRequest from "./Requests/Create";
import StatusBadge from "components/StatusBadge";
import Button from "components/Button";
import DropdownLogic from "components/DropdownLogic";
import { EdeliveryAddressContext } from "dto/EDelivery/EdeliveryAddressContext";
import { EDeliveryPersonalRole } from "dto/EDelivery/EDeliveryPersonalRole";
import { EDeliveryRequestType } from "dto/EDelivery/EDeliveryRequestType";
import NotFoundScreen from "components/NotFoundScreen";
import SettingsIcon from "assets/icons/settings_24px.svg?react";
import { Tooltip } from "@mui/material";
import BackIcon from "assets/icons/back.svg?react";
import ArrowLeftIcon from "assets/icons/arrow_l.svg?react";
import { useOnClickOutside } from "hooks/useClickOutside";
import { IWorkspaceMember } from "dto/IWorkspace";
import SearchMemberInWorkspace from "components/SearchMemberInWorkspace";
import Chip from "components/Chip";
import { Avatar } from "@mui/material";
import { convertToLetters } from "utils/convertToLetters";
import { useAuth } from "react-oidc-context";
import { getUserClaim } from "utils/getUserClaim";
import { Claims } from "dto/User/claims";
import { EUserType } from "dto/User/EUserType";
import Group from "../Group";
import { useTranslation } from "react-i18next";
import DropdownMui from "components/DropdownMui";
import { IDropdownObject } from "components/Dropdown/typings";

const EDeliveryTab: FC<Props> = ({ showTabBar, showRequests = true }) => {
  const { t } = useTranslation();
  const {
    data: edelvieryAddressesData,
    isSuccess: isEDelvieryAddressesLoaded,
    refetch: refetchAdresses,
  } = settingService.useGetEDeliveryAddresses();
  const {
    data: edeliveryRequestsData,
    isSuccess: isEDelvieryRequestsLoaded,
    refetch: refetchRequests,
  } = settingService.useGetEDeliveryRequests();

  const auth = useAuth();
  const isLegalPerson =
    getUserClaim(auth.user, Claims.UserTypeClaim) === EUserType.LegalPerson;

  const [selectedItem, setSelectedItem] = useState<IEDeliveryAddress>(null);

  const { data: addressPrivilegesData } =
    settingService.useGetEDeliveryAddressePrivileges(
      selectedItem?.id ?? 0,
      selectedItem === null,
    );

  const { mutate: addEDeliveryPrivilege } =
    settingService.useAddEDeliveryAddressPrivilege();

  const { mutate: removeEDeliveryPrivilege } =
    settingService.useRemoveEDeliveryAddressPrivilege();

  const [, setShowCreate] = useState<boolean>(false);
  const [showModify, setShowModify] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<IEDeliveryAddress[]>([]);
  const [requests, setRequests] = useState<IEDelvieryRequest[]>([]);

  const [admins, setAdmins] = useState<IEDeliveryAddressUserPrivileges[]>([]);
  const [writers, setWriters] = useState<IEDeliveryAddressUserPrivileges[]>([]);
  const [readers, setReaders] = useState<IEDeliveryAddressUserPrivileges[]>([]);

  const dropdownCreateRef = useRef();
  const modifyDropdownRef = useRef();

  useOnClickOutside(dropdownCreateRef, () => {
    setShowCreate(false);
  });

  const [requestSettings, setRequestSettings] = useState({
    show: false,
    type: EDeliveryRequestType.Create,
    context: EdeliveryAddressContext.Personal,
    role: EDeliveryPersonalRole.Citizen,
    address: null,
  });

  const onInnerViewCanceled = () => {
    setRequestSettings({ ...requestSettings, show: false });
  };

  const onInnerViewSaved = () => {
    setRequestSettings({ ...requestSettings, show: false });
    refetchRequests();
    refetchAdresses();
  };

  useEffect(() => {
    if (isEDelvieryAddressesLoaded) {
      const s = edelvieryAddressesData.data as IEDeliveryAddress[];
      setAddresses(s);
    }
  }, [edelvieryAddressesData]);

  useEffect(() => {
    if (isEDelvieryRequestsLoaded) {
      const s = edeliveryRequestsData.data as IEDelvieryRequest[];
      setRequests(s);
    }
  }, [edeliveryRequestsData]);

  useOnClickOutside(modifyDropdownRef, () => {
    setShowModify(false);
  });

  useEffect(() => {
    if (addressPrivilegesData?.data) {
      setAdmins(addressPrivilegesData?.data.Admin);
      setWriters(addressPrivilegesData?.data.Write);
      setReaders(addressPrivilegesData?.data.Read);
    }
  }, [addressPrivilegesData?.data]);

  const handleModify = (address: IEDeliveryAddress) => {
    setRequestSettings({
      show: true,
      type: EDeliveryRequestType.Update,
      address: address,
      context: !address.isEntrepreneur
        ? EdeliveryAddressContext.Personal
        : EdeliveryAddressContext.Jdg,
      role: address.isTrustedRole
        ? EDeliveryPersonalRole.TrustedRole
        : EDeliveryPersonalRole.Citizen,
    });
    setShowModify(false);
    setSelectedItem(null);
  };

  const handleExtend = (address: IEDeliveryAddress) => {
    setRequestSettings({
      show: true,
      type: EDeliveryRequestType.ExtensePeriod,
      address: address,
      context: !address.isEntrepreneur
        ? EdeliveryAddressContext.Personal
        : EdeliveryAddressContext.Jdg,
      role: address.isTrustedRole
        ? EDeliveryPersonalRole.TrustedRole
        : EDeliveryPersonalRole.Citizen,
    });
    setShowModify(false);
    setSelectedItem(null);
  };

  const handleDisclosure = (address: IEDeliveryAddress) => {
    setRequestSettings({
      show: true,
      type: EDeliveryRequestType.Disclosure,
      address: address,
      context: !address.isEntrepreneur
        ? EdeliveryAddressContext.Personal
        : EdeliveryAddressContext.Jdg,
      role: address.isTrustedRole
        ? EDeliveryPersonalRole.TrustedRole
        : EDeliveryPersonalRole.Citizen,
    });
    setShowModify(false);
    setSelectedItem(null);
  };

  const handleUndisclosure = (address: IEDeliveryAddress) => {
    setRequestSettings({
      show: true,
      type: EDeliveryRequestType.Undisclosure,
      address: address,
      context: !address.isEntrepreneur
        ? EdeliveryAddressContext.Personal
        : EdeliveryAddressContext.Jdg,
      role: address.isTrustedRole
        ? EDeliveryPersonalRole.TrustedRole
        : EDeliveryPersonalRole.Citizen,
    });
    setShowModify(false);
    setSelectedItem(null);
  };

  const handleClose = (address: IEDeliveryAddress) => {
    setRequestSettings({
      show: true,
      type: EDeliveryRequestType.Close,
      address: address,
      context: !address.isEntrepreneur
        ? EdeliveryAddressContext.Personal
        : EdeliveryAddressContext.Jdg,
      role: address.isTrustedRole
        ? EDeliveryPersonalRole.TrustedRole
        : EDeliveryPersonalRole.Citizen,
    });
    setShowModify(false);
    setSelectedItem(null);
  };

  const handleAddUser = (
    member: IWorkspaceMember,
    privilege: EEDeliveryAddressPrivilege,
  ) => {
    addEDeliveryPrivilege(
      {
        AddressId: selectedItem?.id,
        UserId: member.UserId,
        Privilege: privilege,
      },
      {
        onSuccess(data) {
          if (privilege === EEDeliveryAddressPrivilege.Admin) {
            setAdmins([...admins, data.data]);
          } else if (privilege === EEDeliveryAddressPrivilege.Write) {
            setWriters([...writers, data.data]);
          } else if (privilege === EEDeliveryAddressPrivilege.Read) {
            setReaders([...readers, data.data]);
          }
        },
      },
    );
  };
  const handleRemoveUser = (
    member: IEDeliveryAddressUserPrivileges,
    privilege: EEDeliveryAddressPrivilege,
  ) => {
    removeEDeliveryPrivilege(
      {
        AddressId: selectedItem?.id,
        UserId: member.UserId,
        Privilege: privilege,
      },
      {
        onSuccess() {
          if (privilege === EEDeliveryAddressPrivilege.Admin) {
            const newAdmins = [...admins];
            setAdmins(newAdmins.filter((x) => x.UserId !== member.UserId));
          } else if (privilege === EEDeliveryAddressPrivilege.Write) {
            const newWriters = [...writers];
            setWriters(newWriters.filter((x) => x.UserId !== member.UserId));
          } else if (privilege === EEDeliveryAddressPrivilege.Read) {
            const newReaders = [...readers];
            setReaders(newReaders.filter((x) => x.UserId !== member.UserId));
          }
        },
      },
    );
  };

  if (!isEDelvieryAddressesLoaded) {
    return <Spinner></Spinner>;
  }

  return (
    <div className={styles.wrapper}>
      {!selectedItem && (
        <>
          <Group header={t("edeliveryTab.yourAddresses")} initialState={true}>
            <div className={styles.group}>
              <div className={styles.list}>
                {addresses.map((addr) => (
                  <div key={"ade_" + addr.id} className={`${styles.listItem}`}>
                    <div className={styles.left}>
                      <div className={styles.label}>{addr.label}</div>
                      <div className={styles.address}>{addr.address}</div>
                    </div>
                    <div className={styles.right}>
                      {addr.status !== EDeliveryAddressStatus.TRANSFER && (
                        <div className={styles.disclosure}>
                          {addr.isExposed
                            ? t("edeliveryTab.exposed")
                            : t("edeliveryTab.notExposed")}
                        </div>
                      )}

                      {addr.status === EDeliveryAddressStatus.TRANSFER && (
                        <div className={styles.disclosure}>
                          {t("edeliveryTab.transferring")}
                        </div>
                      )}

                      <div className={styles.date}>
                        {moment(addr.creationDate)
                          .format("DD.MM.YYYY")
                          .toString()}
                      </div>
                      <div className={styles.settings}>
                        <Tooltip title={t("edeliveryTab.settings")}>
                          <div
                            onClick={() => {
                              setSelectedItem(addr);
                            }}
                          >
                            <SettingsIcon></SettingsIcon>
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))}

                {!addresses.length && (
                  <div className={styles.empty}>
                    <NotFoundScreen
                      size="small"
                      title={t("edelivery.noAddresses")}
                      text={t("edelivery.addFirstAddress")}
                    ></NotFoundScreen>
                  </div>
                )}
              </div>
            </div>
          </Group>
        </>
      )}

      {showRequests && !selectedItem && (
        <div className={styles.group}>
          <Group header={t("edeliveryTab.requests")} initialState={true}>
            <div className={styles.list}>
              {requests.map((req) => {
                return (
                  <div className={styles.listItem} key={"request" + req.id}>
                    <div className={styles.left}>
                      <div className={styles.label}>{req.label}</div>
                      <div className={styles.address}>{req.sublabel}</div>
                    </div>
                    <div className={styles.right}>
                      {req.status === BaeOperationStatusEnum.SUCCESS && (
                        <StatusBadge
                          onRemove={() => {}}
                          status={{
                            Id: -1,
                            Color: 11,
                            Name: t("edeliveryTab.acceptedCorrectly"),
                            Type: 1,
                            Global: true,
                          }}
                        />
                      )}
                      {req.status === BaeOperationStatusEnum.ERROR && (
                        <StatusBadge
                          onRemove={() => {}}
                          status={{
                            Id: -1,
                            Color: 1,
                            Name: t("registry.badRequest"),
                            Type: 1,
                            Global: true,
                          }}
                        />
                      )}

                      {(req.status === BaeOperationStatusEnum.PENDING ||
                        req.status === BaeOperationStatusEnum.PROCESSING) && (
                        <StatusBadge
                          onRemove={() => {}}
                          status={{
                            Id: -1,
                            Color: 6,
                            Name: t("statuses.pending"),
                            Type: 1,
                            Global: true,
                          }}
                        />
                      )}

                      <div className={styles.date}>
                        {moment(req.creationDate)
                          .format("DD.MM.YYYY")
                          .toString()}
                      </div>
                    </div>
                  </div>
                );
              })}

              {!requests.length && (
                <div className={styles.empty}>{t("registry.noRequests")}</div>
              )}
            </div>
          </Group>
        </div>
      )}

      {requestSettings.show && (
        <div className={styles.requestWrapper}>
          <CreateRequest
            address={requestSettings.address}
            onSaved={onInnerViewSaved}
            onCanceled={onInnerViewCanceled}
            context={requestSettings.context}
            role={requestSettings.role}
            requestType={requestSettings.type}
          ></CreateRequest>
        </div>
      )}

      {selectedItem && (
        <>
          <div className={styles.group}>
            <div className={styles.pathBar}>
              <div className={styles.backButton}>
                <BackIcon onClick={() => setSelectedItem(null)}></BackIcon>
              </div>
              {t("edelivery.title")} <ArrowLeftIcon></ArrowLeftIcon>{" "}
              {t("edeliveryTab.addressConfiguration")}
            </div>

            <div className={styles.list}>
              <div className={`${styles.listItem}`}>
                <div className={styles.left}>
                  <div className={styles.label}>{selectedItem.label}</div>
                  <div className={styles.address}>{selectedItem.address}</div>
                </div>
                <div className={styles.right}>
                  <div className={styles.disclosure}>
                    {selectedItem.isExposed
                      ? t("edeliveryTab.exposed")
                      : t("edeliveryTab.notExposed")}
                  </div>

                  <div className={styles.date}>
                    {moment(selectedItem.creationDate)
                      .format("DD.MM.YYYY")
                      .toString()}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.btnWrapper}>
              <Button
                className={styles.btn}
                onClick={() => {
                  setShowModify(!showModify);
                }}
                text={t("edelivery.submitRequest")}
              />

              {showModify && (
                <div className={styles.dropdownWrapper} ref={modifyDropdownRef}>
                  <DropdownLogic>
                    <div className={`${styles.createDropdown}`}>
                      {selectedItem.expireSoon && (
                        <div
                          className={styles.item}
                          onClick={() => {
                            handleExtend(selectedItem);
                          }}
                        >
                          {t("settings.extendValidity")}
                        </div>
                      )}
                      <div
                        className={styles.item}
                        onClick={() => {
                          handleModify(selectedItem);
                        }}
                      >
                        {t("edeliveryTab.updateData")}
                      </div>
                      {!selectedItem.isExposed && (
                        <div
                          className={styles.item}
                          onClick={() => {
                            handleDisclosure(selectedItem);
                          }}
                        >
                          {t("edeliveryTab.disclosureAddress")}
                        </div>
                      )}
                      {selectedItem.isExposed && (
                        <div
                          className={styles.item}
                          onClick={() => {
                            handleUndisclosure(selectedItem);
                          }}
                        >
                          {t("edeliveryTab.undisclosureAddress")}
                        </div>
                      )}
                      <div
                        className={styles.item}
                        onClick={() => {
                          handleClose(selectedItem);
                        }}
                      >
                        {t("settings.deleteAddress")}
                      </div>
                    </div>
                  </DropdownLogic>
                </div>
              )}
            </div>
          </div>


          <div className={styles.group}>
            <div className={styles.header}>{t("settings.mailboxAdmin")}</div>
            <div className={styles.hint}>
              {t("settings.manageMailboxLabel")}
            </div>
            <SearchMemberInWorkspace
              onChoose={(member) => {
                handleAddUser(member, EEDeliveryAddressPrivilege.Admin);
              }}
              clearAfterChoose={true}
              label={t("settings.findUserLabel")}
              showMe={!isLegalPerson}
            />
            <div className={styles.members}>
              {admins.map((member) => (
                <Chip
                  key={member.UserId}
                  avatar={
                    <Avatar className={styles.avatar}>
                      {convertToLetters(member.Label)}
                    </Avatar>
                  }
                  element={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {member.Label}
                    </div>
                  }
                  onDelete={
                    member.Fixed
                      ? undefined
                      : () => {
                          handleRemoveUser(
                            member,
                            EEDeliveryAddressPrivilege.Admin,
                          );
                        }
                  }
                />
              ))}
            </div>

            <div className={styles.header}>
              {t("edeliveryTab.receivePermissions")}
            </div>
            <div className={styles.hint}>
              {t("edeliveryTab.receiversLabelTypo")}
            </div>
            <SearchMemberInWorkspace
              onChoose={(member) => {
                handleAddUser(member, EEDeliveryAddressPrivilege.Read);
              }}
              clearAfterChoose={true}
              label={t("settings.findUserLabel")}
              showMe={!isLegalPerson}
            />
            <div className={styles.members}>
              {readers.map((member) => (
                <Chip
                  key={member.UserId}
                  avatar={
                    <Avatar className={styles.avatar}>
                      {convertToLetters(member.Label)}
                    </Avatar>
                  }
                  element={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {member.Label}
                    </div>
                  }
                  onDelete={
                    member.Fixed
                      ? undefined
                      : () => {
                          handleRemoveUser(
                            member,
                            EEDeliveryAddressPrivilege.Read,
                          );
                        }
                  }
                />
              ))}
            </div>

            <div className={styles.header}>{t("settings.sendPermissions")}</div>
            <div className={styles.hint}>
              {t("edeliveryTab.sendersLabelTypo")}
            </div>
            <SearchMemberInWorkspace
              onChoose={(member) => {
                handleAddUser(member, EEDeliveryAddressPrivilege.Write);
              }}
              clearAfterChoose={true}
              label={t("settings.findUserLabel")}
              showMe={!isLegalPerson}
            />
            <div className={styles.members}>
              {writers.map((member) => (
                <Chip
                  key={member.UserId}
                  avatar={
                    <Avatar className={styles.avatar}>
                      {convertToLetters(member.Label)}
                    </Avatar>
                  }
                  element={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {member.Label}
                    </div>
                  }
                  onDelete={
                    member.Fixed
                      ? undefined
                      : () => {
                          handleRemoveUser(
                            member,
                            EEDeliveryAddressPrivilege.Write,
                          );
                        }
                  }
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EDeliveryTab;
